/**
 * Chance Radar templates.
 *
 * Clicking one fills the hunt, the window, the radius and the search query.
 * The query is only sent while the hunt is still the template text. A rewritten
 * hunt drops the query, and the search writes one of its own.
 * Windows stay in lockstep with functions/helpers/chanceRadarSpec.js.
 */

export const CHANCE_WINDOWS = [7, 30, 90, 180];

export const CHANCE_RADII = [
  { km: 40, label: "40 km" },
  { km: 80, label: "80 km" },
  { km: 150, label: "150 km" },
  { km: 0, label: "Whole country" },
];

export const CHANCE_TEMPLATES = [
  {
    id: "productLaunch",
    name: "New product line",
    pitch: "A company nearby just launched a product, a variety or a redesign and may need what your contact sells.",
    topic: "New product line",
    count: 3,
    radiusKm: 80,
    windowDays: 90,
    firstStep: "Open with the launch itself: what came out, when, and why a conversation this month is timely.",
    query: '{{city}} (Produktlaunch OR "neue Produktlinie" OR Relaunch OR "jetzt erhältlich" OR "product launch" OR "now available") {{year}}',
    hunt:
      "Find companies that, inside the window and the area, launched a product, a new variety, a redesign or a new SKU. They should plausibly need what THIS prospect sells (read the offer from the prospect: packaging, labels, ingredients, parts, print, or whatever it is). Each chance is a potential customer of the prospect, not a competitor and not the prospect.",
  },
  {
    id: "newSite",
    name: "New location",
    pitch: "An opening, a renovation, a second site or a new plant that needs what your contact sells.",
    topic: "New location",
    count: 3,
    radiusKm: 80,
    windowDays: 30,
    firstStep: "Name the new site and offer a concrete next step while the project is still being set up.",
    query: '{{city}} (Neueröffnung OR eröffnet OR Umbau OR "neuer Standort" OR "now open" OR renovation) {{year}}',
    hunt:
      "Find companies that opened, renovated or announced a new site, shop, plant, pop-up or second location inside the window and the area. The project should plausibly need what this prospect sells, such as fit-out, signage, furniture, security, cleaning or a local service. Potential customers of the prospect, not the prospect.",
  },
  {
    id: "hiring",
    name: "Hiring for it",
    pitch: "A role posted recently that sits next to your contact's offer. The last 7 days rank first.",
    topic: "Hiring",
    count: 3,
    radiusKm: 80,
    windowDays: 7,
    firstStep: "Mention the role by name and why their team does not have to cover it alone.",
    query: '{{city}} (Stellenanzeige OR "wir suchen" OR "jetzt einstellen" OR hiring OR "job opening") {{year}}',
    hunt:
      "Find companies that posted a job the prospect's offer would support or sit beside. Name the role. Skip generic we-are-hiring posts with no role. A role from the last few days outranks an older posting. Potential customers of the prospect, not the prospect.",
  },
  {
    id: "deadline",
    name: "Tender or deadline",
    pitch: "A dated tender, law or deadline their buyers have to act on.",
    topic: "Tender or deadline",
    count: 3,
    radiusKm: 0,
    windowDays: 90,
    firstStep: "Lead with the date and what has to be done before it.",
    query: '{{country}} (Ausschreibung OR Vergabe OR Frist OR tender OR deadline) {{year}}',
    hunt:
      "Find a tender, a law or a dated deadline the prospect's buyers have to act on. The date has to be in the source. Skip undated commentary. The company named should be one the prospect could sell to, or a buyer group the prospect already serves.",
  },
  {
    id: "season",
    name: "Fair and season",
    pitch: "A fair, a season opening or a local event their buyers will show up at.",
    topic: "Fair and season",
    count: 3,
    radiusKm: 0,
    windowDays: 90,
    firstStep: "Name the event and the date, and tie it to one thing the prospect sells.",
    query: '{{country}} (Messe OR Fachmesse OR Saisonstart OR "trade fair" OR "season opening") {{year}}',
    hunt:
      "Find a trade fair, a season opening or a local event the prospect's buyers will attend, with a date inside the window. Tie it to what the prospect sells. Name the organizer or a company that is publicly taking part.",
  },
  {
    id: "expansion",
    name: "Expansion",
    pitch: "Funding, a new building, more capacity or a second shift at a company that would buy from them.",
    topic: "Expansion",
    count: 3,
    radiusKm: 0,
    windowDays: 180,
    firstStep: "Name what is being built and why the next few months are the moment to call.",
    query: '{{country}} (Expansion OR Neubau OR Finanzierung OR Kapazität OR "series a" OR "groundbreaking") {{year}}',
    hunt:
      "Find funding, a new building, added capacity or a second shift at a company that would buy what the prospect sells. Name what is being built or funded. Potential customers of the prospect, not the prospect.",
  },
  {
    id: "custom",
    name: "Custom search",
    pitch: "Describe the live signal yourself. The window, the radius and the count stay yours.",
    topic: "Custom search",
    count: 3,
    radiusKm: 80,
    windowDays: 30,
    firstStep: "One concrete opening their sales team can use this week.",
    query: "",
    hunt: "",
  },
];

export const templateById = (id) => CHANCE_TEMPLATES.find((template) => template.id === id) || CHANCE_TEMPLATES[0];

/** The canned query is only safe while the hunt is still the template text. */
export const queryForHunt = (templateId, hunt) => {
  const template = CHANCE_TEMPLATES.find((item) => item.id === templateId);
  if (!template || !template.query) return "";
  return hunt.trim() === template.hunt.trim() ? template.query : "";
};
